import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { configApi } from 'Api';
import {
  DateFormat,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_TIME_FORMAT,
  DEFAULT_TIME_STEP,
  DisplayMode,
  TimeFormat
} from 'Utils/dateTime';

import {
  DEFAULT_BORDER_SIZE,
  DEFAULT_FONT_SIZE,
  DEFAULT_TAG_COLOR_MODE,
  DEFAULT_TAG_VARIANT,
  DEFAULT_UNIFORM_COLOR,
  TagColorMode,
  TagVariant
} from './tagsConfig';

import type {
  FormsDefaultValuesSettings,
  FormsDefaultValuesSettingsFreshStartForm,
  GoogleSettings,
  SentrySettings
} from 'Api';
import type { RootState } from 'Store';

export interface TagStylingSettings {
  uniformColor: string;
  borderSize: number;
  fontSize: number;
}

export interface TagsSettings {
  variant: TagVariant;
  colorMode: TagColorMode;
  styling: TagStylingSettings;
}

export interface DateTimePickerSettings {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  displayMode: DisplayMode;
  timeStep: number;
  mondayFirst: boolean;
}

export interface ListPaginationSettings {
  pageSize: number;
  maxPageSize: number;
}

export interface PaginationSettings {
  global: {
    pageSizeOptions: number[];
    showFirstLastButtons: boolean;
    showPreviousNextButtons: boolean;
    showPageSizeSelector: boolean;
    showPageInfo: boolean;
  };
  lists: {
    courses: ListPaginationSettings;
  };
}

export interface AppConfigState {
  sentrySettings: SentrySettings;
  googleSettings: GoogleSettings;
  formsDefaultValuesSettings: FormsDefaultValuesSettings;
  paginationSettings: PaginationSettings;
  tagsSettings: TagsSettings;
  dateTimePickerSettings: DateTimePickerSettings;
  isConfigLoading: boolean;
  configLoadError: string | null;
}

const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 10000;
const MIN_BORDER_SIZE = 1;
const MAX_BORDER_SIZE = 10;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 24;

const validateNumberInRange = (
  value: unknown,
  min: number,
  max: number,
  defaultValue: number
): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || isNaN(value)) {
    return defaultValue;
  }

  return Math.max(min, Math.min(Math.floor(value), max));
};

const validateBooleanValue = (value: unknown, defaultValue: boolean): boolean =>
  typeof value === 'boolean' ? value : defaultValue;

const validateColorValue = (value: unknown, defaultValue: string): string => {
  if (typeof value === 'string') {
    // Accept both 6-digit and 3-digit hex colors, and convert 3-digit to 6-digit
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      return value;
    }
    if (/^#[0-9A-Fa-f]{3}$/.test(value)) {
      // Convert 3-digit hex to 6-digit hex
      const hex = value.slice(1);
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }
  }
  return defaultValue;
};

const validateTagStylingSettings = (value: unknown): TagStylingSettings => {
  const defaults = {
    uniformColor: DEFAULT_UNIFORM_COLOR,
    borderSize: DEFAULT_BORDER_SIZE,
    fontSize: DEFAULT_FONT_SIZE
  };

  if (!value || typeof value !== 'object') {
    return defaults;
  }

  const settings = value as Record<string, unknown>;

  return {
    uniformColor: validateColorValue(settings.uniformColor, defaults.uniformColor),
    borderSize: validateNumberInRange(
      settings.borderSize,
      MIN_BORDER_SIZE,
      MAX_BORDER_SIZE,
      defaults.borderSize
    ),
    fontSize: validateNumberInRange(
      settings.fontSize,
      MIN_FONT_SIZE,
      MAX_FONT_SIZE,
      defaults.fontSize
    )
  };
};

const validateDateFormat = (value: unknown): DateFormat => {
  if (typeof value === 'string' && Object.values(DateFormat).includes(value as DateFormat)) {
    return value as DateFormat;
  }

  return DEFAULT_DATE_FORMAT;
};

const validateTimeFormat = (value: unknown): TimeFormat => {
  if (typeof value === 'string' && Object.values(TimeFormat).includes(value as TimeFormat)) {
    return value as TimeFormat;
  }
  return DEFAULT_TIME_FORMAT;
};

const validateDisplayMode = (value: unknown): DisplayMode => {
  if (typeof value === 'string' && Object.values(DisplayMode).includes(value as DisplayMode)) {
    return value as DisplayMode;
  }
  return DEFAULT_DISPLAY_MODE;
};

const validateDateTimePickerSettings = (value: unknown): DateTimePickerSettings => {
  const defaults = {
    dateFormat: DEFAULT_DATE_FORMAT,
    timeFormat: DEFAULT_TIME_FORMAT,
    displayMode: DEFAULT_DISPLAY_MODE,
    timeStep: DEFAULT_TIME_STEP,
    mondayFirst: true
  };

  if (!value || typeof value !== 'object') {
    return defaults;
  }

  const settings = value as Record<string, unknown>;

  return {
    dateFormat: validateDateFormat(settings.dateFormat),
    timeFormat: validateTimeFormat(settings.timeFormat),
    displayMode: validateDisplayMode(settings.displayMode),
    timeStep: validateNumberInRange(
      settings.timeStep,
      60, // 1 minute minimum
      3600, // 1 hour maximum
      defaults.timeStep
    ),
    mondayFirst: validateBooleanValue(settings.mondayFirst, defaults.mondayFirst)
  };
};

const validateArrayOfNumbers = (
  value: unknown,
  defaultValue: number[],
  min: number = MIN_PAGE_SIZE,
  max: number = MAX_PAGE_SIZE
): number[] => {
  if (!Array.isArray(value)) {
    return defaultValue;
  }

  const validatedArray = value
    .filter(item => typeof item === 'number' && Number.isFinite(item) && !isNaN(item))
    .map(item => Math.max(min, Math.min(Math.floor(item), max)))
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .sort((a, b) => a - b);

  return validatedArray.length > 0 ? validatedArray : defaultValue;
};

const validateGlobalPaginationSettings = (value: unknown): PaginationSettings['global'] => {
  const defaults = {
    pageSizeOptions: [5, 10, 20, 50, 100],
    showFirstLastButtons: true,
    showPreviousNextButtons: true,
    showPageSizeSelector: true,
    showPageInfo: true
  };

  if (!value || typeof value !== 'object') {
    return defaults;
  }

  const settings = value as Record<string, unknown>;

  return {
    pageSizeOptions: validateArrayOfNumbers(
      settings.pageSizeOptions,
      defaults.pageSizeOptions,
      MIN_PAGE_SIZE,
      MAX_PAGE_SIZE
    ),
    showFirstLastButtons: validateBooleanValue(
      settings.showFirstLastButtons,
      defaults.showFirstLastButtons
    ),
    showPreviousNextButtons: validateBooleanValue(
      settings.showPreviousNextButtons,
      defaults.showPreviousNextButtons
    ),
    showPageSizeSelector: validateBooleanValue(
      settings.showPageSizeSelector,
      defaults.showPageSizeSelector
    ),
    showPageInfo: validateBooleanValue(settings.showPageInfo, defaults.showPageInfo)
  };
};

const validateListPaginationSettings = (
  value: unknown,
  defaultSettings: ListPaginationSettings
): ListPaginationSettings => {
  if (!value || typeof value !== 'object') {
    return defaultSettings;
  }

  const settings = value as Record<string, unknown>;

  return {
    pageSize: validateNumberInRange(
      settings.pageSize,
      MIN_PAGE_SIZE,
      MAX_PAGE_SIZE,
      defaultSettings.pageSize
    ),
    maxPageSize: validateNumberInRange(
      settings.maxPageSize,
      MIN_PAGE_SIZE,
      MAX_PAGE_SIZE,
      defaultSettings.maxPageSize
    )
  };
};

export const createDefaultListPaginationSettings = (
  pageSize: number = 20
): ListPaginationSettings => ({
  pageSize: validateNumberInRange(pageSize, MIN_PAGE_SIZE, MAX_PAGE_SIZE, 20),
  maxPageSize: 100
});

const createDefaultPaginationSettings = (): PaginationSettings => ({
  global: {
    pageSizeOptions: [5, 10, 20, 50, 100],
    showFirstLastButtons: true,
    showPreviousNextButtons: true,
    showPageSizeSelector: true,
    showPageInfo: true
  },
  lists: {
    courses: {
      pageSize: 20,
      maxPageSize: 100
    }
  }
});

const loadPaginationSettingsFromStorage = (): PaginationSettings => {
  try {
    const stored = localStorage.getItem('paginationSettings');
    if (!stored) {
      return createDefaultPaginationSettings();
    }

    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') {
      return createDefaultPaginationSettings();
    }

    const defaults = createDefaultPaginationSettings();

    return {
      global: validateGlobalPaginationSettings(parsed.global),
      lists: {
        courses: validateListPaginationSettings(parsed.lists?.courses, defaults.lists.courses)
      }
    };
  } catch (error) {
    console.warn('Failed to load pagination settings from localStorage:', error);
    return createDefaultPaginationSettings();
  }
};

const savePaginationSettingsToStorage = (settings: PaginationSettings): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const serialized = JSON.stringify(settings);
    if (serialized.length > 50000) {
      console.warn('Pagination settings too large to store, skipping');
      return;
    }

    localStorage.setItem('paginationSettings', serialized);
  } catch (error) {
    console.warn('Failed to save pagination settings to localStorage:', error);
  }
};

const loadTagsSettingsFromStorage = (): TagsSettings => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {
        variant: DEFAULT_TAG_VARIANT,
        colorMode: DEFAULT_TAG_COLOR_MODE,
        styling: {
          uniformColor: DEFAULT_UNIFORM_COLOR,
          borderSize: DEFAULT_BORDER_SIZE,
          fontSize: DEFAULT_FONT_SIZE
        }
      };
    }

    const stored = localStorage.getItem('tagsSettings');
    if (stored) {
      const parsed = JSON.parse(stored);

      return {
        variant: Object.values(TagVariant).includes(parsed.variant)
          ? parsed.variant
          : DEFAULT_TAG_VARIANT,
        colorMode: Object.values(TagColorMode).includes(parsed.colorMode)
          ? parsed.colorMode
          : DEFAULT_TAG_COLOR_MODE,
        styling: validateTagStylingSettings(parsed.styling)
      };
    }
  } catch (error) {
    console.warn('Failed to load tags settings from localStorage:', error);
  }

  return {
    variant: DEFAULT_TAG_VARIANT,
    colorMode: DEFAULT_TAG_COLOR_MODE,
    styling: {
      uniformColor: DEFAULT_UNIFORM_COLOR,
      borderSize: DEFAULT_BORDER_SIZE,
      fontSize: DEFAULT_FONT_SIZE
    }
  };
};

const loadDateTimePickerSettingsFromStorage = (): DateTimePickerSettings => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {
        dateFormat: DEFAULT_DATE_FORMAT,
        timeFormat: DEFAULT_TIME_FORMAT,
        displayMode: DEFAULT_DISPLAY_MODE,
        timeStep: DEFAULT_TIME_STEP,
        mondayFirst: true
      };
    }

    const stored = localStorage.getItem('dateTimePickerSettings');
    if (stored) {
      const parsed = JSON.parse(stored);
      return validateDateTimePickerSettings(parsed);
    }
  } catch (error) {
    console.warn('Failed to load DateTimePicker settings from localStorage:', error);
  }

  return {
    dateFormat: DEFAULT_DATE_FORMAT,
    timeFormat: DEFAULT_TIME_FORMAT,
    displayMode: DEFAULT_DISPLAY_MODE,
    timeStep: DEFAULT_TIME_STEP,
    mondayFirst: true
  };
};

const saveTagsSettingsToStorage = (settings: TagsSettings) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const serialized = JSON.stringify(settings);
    localStorage.setItem('tagsSettings', serialized);
  } catch (error) {
    console.warn('Failed to save tags settings to localStorage:', error);
  }
};

const saveDateTimePickerSettingsToStorage = (settings: DateTimePickerSettings) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const serialized = JSON.stringify(settings);
    localStorage.setItem('dateTimePickerSettings', serialized);
  } catch (error) {
    console.warn('Failed to save DateTimePicker settings to localStorage:', error);
  }
};

const initialState: AppConfigState = {
  sentrySettings: {} as SentrySettings,
  googleSettings: {} as GoogleSettings,
  formsDefaultValuesSettings: {
    freshStartForm: {} as FormsDefaultValuesSettingsFreshStartForm
  } as FormsDefaultValuesSettings,
  paginationSettings: loadPaginationSettingsFromStorage(),
  tagsSettings: loadTagsSettingsFromStorage(),
  dateTimePickerSettings: loadDateTimePickerSettingsFromStorage(),
  isConfigLoading: false,
  configLoadError: null
};

export const getAppConfig = createAsyncThunk(
  'config/getAppConfig',
  async (_, { rejectWithValue }) => {
    try {
      const result = await configApi.getConfig();

      if (!result.isSuccess) {
        throw new Error(`Config API request failed: ${result.error ?? 'Unknown error'}`);
      }

      if (!result.data) {
        throw new Error('Config API returned empty data');
      }

      return result.data;
    } catch (error: any) {
      console.error('Failed to load app configuration:', error);
      return rejectWithValue(error.message ?? 'Failed to load configuration');
    }
  }
);

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    clearConfigError: state => {
      state.configLoadError = null;
    },

    setListPaginationPageSize: (
      state,
      action: PayloadAction<{ listName: keyof PaginationSettings['lists']; pageSize: number }>
    ) => {
      const { listName, pageSize } = action.payload;

      if (!listName || typeof pageSize !== 'number') {
        return;
      }

      const listSettings = state.paginationSettings.lists[listName];
      if (listSettings) {
        const validatedPageSize = validateNumberInRange(
          pageSize,
          MIN_PAGE_SIZE,
          listSettings.maxPageSize,
          listSettings.pageSize
        );
        listSettings.pageSize = validatedPageSize;
        savePaginationSettingsToStorage(state.paginationSettings);
      }
    },

    setTagsSettings: (state, action: PayloadAction<Partial<TagsSettings>>) => {
      if (!state.tagsSettings.styling) {
        state.tagsSettings.styling = {
          uniformColor: DEFAULT_UNIFORM_COLOR,
          borderSize: DEFAULT_BORDER_SIZE,
          fontSize: DEFAULT_FONT_SIZE
        };
      }
      state.tagsSettings = { ...state.tagsSettings, ...action.payload };
      saveTagsSettingsToStorage(state.tagsSettings);
    },

    setTagVariant: (state, action: PayloadAction<TagVariant>) => {
      state.tagsSettings.variant = action.payload;
      saveTagsSettingsToStorage(state.tagsSettings);
    },

    setTagColorMode: (state, action: PayloadAction<TagColorMode>) => {
      state.tagsSettings.colorMode = action.payload;
      saveTagsSettingsToStorage(state.tagsSettings);
    },

    setTagStyling: (state, action: PayloadAction<Partial<TagStylingSettings>>) => {
      if (!state.tagsSettings.styling) {
        state.tagsSettings.styling = {
          uniformColor: DEFAULT_UNIFORM_COLOR,
          borderSize: DEFAULT_BORDER_SIZE,
          fontSize: DEFAULT_FONT_SIZE
        };
      }
      state.tagsSettings.styling = { ...state.tagsSettings.styling, ...action.payload };
      saveTagsSettingsToStorage(state.tagsSettings);
    },

    setTagUniformColor: (state, action: PayloadAction<string>) => {
      if (!state.tagsSettings.styling) {
        state.tagsSettings.styling = {
          uniformColor: DEFAULT_UNIFORM_COLOR,
          borderSize: DEFAULT_BORDER_SIZE,
          fontSize: DEFAULT_FONT_SIZE
        };
      }
      state.tagsSettings.styling.uniformColor = validateColorValue(
        action.payload,
        state.tagsSettings.styling.uniformColor
      );
      saveTagsSettingsToStorage(state.tagsSettings);
    },

    setTagBorderSize: (state, action: PayloadAction<number>) => {
      if (!state.tagsSettings.styling) {
        state.tagsSettings.styling = {
          uniformColor: DEFAULT_UNIFORM_COLOR,
          borderSize: DEFAULT_BORDER_SIZE,
          fontSize: DEFAULT_FONT_SIZE
        };
      }
      state.tagsSettings.styling.borderSize = validateNumberInRange(
        action.payload,
        MIN_BORDER_SIZE,
        MAX_BORDER_SIZE,
        state.tagsSettings.styling.borderSize
      );
      saveTagsSettingsToStorage(state.tagsSettings);
    },

    setTagFontSize: (state, action: PayloadAction<number>) => {
      if (!state.tagsSettings.styling) {
        state.tagsSettings.styling = {
          uniformColor: DEFAULT_UNIFORM_COLOR,
          borderSize: DEFAULT_BORDER_SIZE,
          fontSize: DEFAULT_FONT_SIZE
        };
      }
      state.tagsSettings.styling.fontSize = validateNumberInRange(
        action.payload,
        MIN_FONT_SIZE,
        MAX_FONT_SIZE,
        state.tagsSettings.styling.fontSize
      );
      saveTagsSettingsToStorage(state.tagsSettings);
    },

    resetTagsSettings: state => {
      state.tagsSettings = loadTagsSettingsFromStorage();
    },

    setDateTimePickerSettings: (state, action: PayloadAction<Partial<DateTimePickerSettings>>) => {
      if (!state.dateTimePickerSettings) {
        state.dateTimePickerSettings = {
          dateFormat: DEFAULT_DATE_FORMAT,
          timeFormat: DEFAULT_TIME_FORMAT,
          displayMode: DEFAULT_DISPLAY_MODE,
          timeStep: DEFAULT_TIME_STEP,
          mondayFirst: true
        };
      }
      state.dateTimePickerSettings = { ...state.dateTimePickerSettings, ...action.payload };
      saveDateTimePickerSettingsToStorage(state.dateTimePickerSettings);
    },

    setDateTimeDateFormat: (state, action: PayloadAction<DateFormat>) => {
      if (!state.dateTimePickerSettings) {
        state.dateTimePickerSettings = {
          dateFormat: DEFAULT_DATE_FORMAT,
          timeFormat: DEFAULT_TIME_FORMAT,
          displayMode: DEFAULT_DISPLAY_MODE,
          timeStep: DEFAULT_TIME_STEP,
          mondayFirst: true
        };
      }
      state.dateTimePickerSettings.dateFormat = action.payload;
      saveDateTimePickerSettingsToStorage(state.dateTimePickerSettings);
    },

    setDateTimeTimeFormat: (state, action: PayloadAction<TimeFormat>) => {
      if (!state.dateTimePickerSettings) {
        state.dateTimePickerSettings = {
          dateFormat: DEFAULT_DATE_FORMAT,
          timeFormat: DEFAULT_TIME_FORMAT,
          displayMode: DEFAULT_DISPLAY_MODE,
          timeStep: DEFAULT_TIME_STEP,
          mondayFirst: true
        };
      }
      state.dateTimePickerSettings.timeFormat = action.payload;
      saveDateTimePickerSettingsToStorage(state.dateTimePickerSettings);
    },

    setDateTimeDisplayMode: (state, action: PayloadAction<DisplayMode>) => {
      if (!state.dateTimePickerSettings) {
        state.dateTimePickerSettings = {
          dateFormat: DEFAULT_DATE_FORMAT,
          timeFormat: DEFAULT_TIME_FORMAT,
          displayMode: DEFAULT_DISPLAY_MODE,
          timeStep: DEFAULT_TIME_STEP,
          mondayFirst: true
        };
      }
      state.dateTimePickerSettings.displayMode = action.payload;
      saveDateTimePickerSettingsToStorage(state.dateTimePickerSettings);
    },

    setDateTimeTimeStep: (state, action: PayloadAction<number>) => {
      if (!state.dateTimePickerSettings) {
        state.dateTimePickerSettings = {
          dateFormat: DEFAULT_DATE_FORMAT,
          timeFormat: DEFAULT_TIME_FORMAT,
          displayMode: DEFAULT_DISPLAY_MODE,
          timeStep: DEFAULT_TIME_STEP,
          mondayFirst: true
        };
      }
      state.dateTimePickerSettings.timeStep = validateNumberInRange(
        action.payload,
        60,
        3600,
        state.dateTimePickerSettings.timeStep
      );
      saveDateTimePickerSettingsToStorage(state.dateTimePickerSettings);
    },

    setDateTimeUse24HourFormat: (state, action: PayloadAction<boolean>) => {
      if (!state.dateTimePickerSettings) {
        state.dateTimePickerSettings = {
          dateFormat: DEFAULT_DATE_FORMAT,
          timeFormat: DEFAULT_TIME_FORMAT,
          displayMode: DEFAULT_DISPLAY_MODE,
          timeStep: DEFAULT_TIME_STEP,
          mondayFirst: true
        };
      }
      state.dateTimePickerSettings.timeFormat = action.payload ? TimeFormat.H24 : TimeFormat.H12;
      saveDateTimePickerSettingsToStorage(state.dateTimePickerSettings);
    },

    setDateTimeMondayFirst: (state, action: PayloadAction<boolean>) => {
      if (!state.dateTimePickerSettings) {
        state.dateTimePickerSettings = {
          dateFormat: DEFAULT_DATE_FORMAT,
          timeFormat: DEFAULT_TIME_FORMAT,
          displayMode: DEFAULT_DISPLAY_MODE,
          timeStep: DEFAULT_TIME_STEP,
          mondayFirst: true
        };
      }
      state.dateTimePickerSettings.mondayFirst = action.payload;
      saveDateTimePickerSettingsToStorage(state.dateTimePickerSettings);
    },

    resetDateTimePickerSettings: state => {
      state.dateTimePickerSettings = loadDateTimePickerSettingsFromStorage();
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAppConfig.pending, state => {
        state.isConfigLoading = true;
        state.configLoadError = null;
      })
      .addCase(getAppConfig.fulfilled, (state, action) => {
        state.isConfigLoading = false;
        state.configLoadError = null;
        state.sentrySettings = action.payload.sentrySettings ?? {};
        state.googleSettings = action.payload.googleSettings ?? {};
        state.formsDefaultValuesSettings = action.payload.formsDefaultValuesSettings ?? {
          freshStartForm: {} as FormsDefaultValuesSettingsFreshStartForm
        };
      })
      .addCase(getAppConfig.rejected, (state, action) => {
        state.isConfigLoading = false;
        state.configLoadError = action.payload as string;
      });
  }
});

export const {
  clearConfigError,
  setListPaginationPageSize,
  setTagsSettings,
  setTagVariant,
  setTagColorMode,
  setTagStyling,
  setTagUniformColor,
  setTagBorderSize,
  setTagFontSize,
  resetTagsSettings,
  setDateTimePickerSettings,
  setDateTimeDateFormat,
  setDateTimeTimeFormat,
  setDateTimeDisplayMode,
  setDateTimeTimeStep,
  setDateTimeUse24HourFormat,
  setDateTimeMondayFirst,
  resetDateTimePickerSettings
} = configSlice.actions;

export const selectSentrySettings = (state: RootState): SentrySettings =>
  state.config.sentrySettings ?? {};

export const selectGoogleSettings = (state: RootState): GoogleSettings =>
  state.config.googleSettings ?? {};

export const selectFormsDefaultValuesSettings = (state: RootState): FormsDefaultValuesSettings =>
  state.config.formsDefaultValuesSettings ?? {
    freshStartForm: {} as FormsDefaultValuesSettingsFreshStartForm
  };

export const selectIsConfigLoading = (state: RootState): boolean => state.config.isConfigLoading;

export const selectConfigLoadError = (state: RootState): string | null =>
  state.config.configLoadError;

export const selectShouldShowExternalLoginProviders = (state: RootState): boolean =>
  !!(state.config.googleSettings?.clientId && state.config.googleSettings.clientId.trim() !== '');

export const selectFreshStartFormDefaultValues = (state: RootState) =>
  state.config.formsDefaultValuesSettings?.freshStartForm ?? {};

export const selectPaginationSettings = (state: RootState): PaginationSettings =>
  state.config?.paginationSettings ?? loadPaginationSettingsFromStorage();

export const selectGlobalPaginationSettings = (state: RootState) =>
  selectPaginationSettings(state).global;

export const selectListPaginationSettings = (
  state: RootState,
  listName: keyof PaginationSettings['lists']
): ListPaginationSettings => {
  if (!state.config?.paginationSettings?.lists) {
    return createDefaultPaginationSettings().lists[listName];
  }

  return state.config.paginationSettings.lists[listName];
};

export const selectCoursesPaginationSettings = (state: RootState) =>
  selectListPaginationSettings(state, 'courses');

export const selectTagsSettings = (state: RootState): TagsSettings =>
  state.config?.tagsSettings ?? {
    variant: DEFAULT_TAG_VARIANT,
    colorMode: DEFAULT_TAG_COLOR_MODE,
    styling: {
      uniformColor: DEFAULT_UNIFORM_COLOR,
      borderSize: DEFAULT_BORDER_SIZE,
      fontSize: DEFAULT_FONT_SIZE
    }
  };

export const selectTagVariant = (state: RootState): TagVariant =>
  state.config?.tagsSettings?.variant ?? DEFAULT_TAG_VARIANT;

export const selectTagColorMode = (state: RootState): TagColorMode =>
  state.config?.tagsSettings?.colorMode ?? DEFAULT_TAG_COLOR_MODE;

export const selectTagStyling = (state: RootState): TagStylingSettings =>
  state.config?.tagsSettings?.styling ?? {
    uniformColor: DEFAULT_UNIFORM_COLOR,
    borderSize: DEFAULT_BORDER_SIZE,
    fontSize: DEFAULT_FONT_SIZE
  };

export const selectTagUniformColor = (state: RootState): string =>
  state.config?.tagsSettings?.styling?.uniformColor ?? DEFAULT_UNIFORM_COLOR;

export const selectTagBorderSize = (state: RootState): number =>
  state.config?.tagsSettings?.styling?.borderSize ?? DEFAULT_BORDER_SIZE;

export const selectTagFontSize = (state: RootState): number =>
  state.config?.tagsSettings?.styling?.fontSize ?? DEFAULT_FONT_SIZE;

export const selectDateTimePickerSettings = (state: RootState): DateTimePickerSettings =>
  state.config?.dateTimePickerSettings ?? {
    dateFormat: DEFAULT_DATE_FORMAT,
    timeFormat: DEFAULT_TIME_FORMAT,
    displayMode: DEFAULT_DISPLAY_MODE,
    timeStep: DEFAULT_TIME_STEP,
    mondayFirst: true
  };

export const selectDateTimeDateFormat = (state: RootState): DateFormat =>
  state.config?.dateTimePickerSettings?.dateFormat ?? DEFAULT_DATE_FORMAT;

export const selectDateTimeTimeFormat = (state: RootState): TimeFormat =>
  state.config?.dateTimePickerSettings?.timeFormat ?? DEFAULT_TIME_FORMAT;

export const selectDateTimeDisplayMode = (state: RootState): DisplayMode =>
  state.config?.dateTimePickerSettings?.displayMode ?? DEFAULT_DISPLAY_MODE;

export const selectDateTimeTimeStep = (state: RootState): number =>
  state.config?.dateTimePickerSettings?.timeStep ?? DEFAULT_TIME_STEP;

export const selectDateTimeMondayFirst = (state: RootState): boolean =>
  state.config?.dateTimePickerSettings?.mondayFirst ?? true;

export const configReducer = configSlice.reducer;
